import { useEffect, useMemo, useRef, useState } from 'react'
import { eur } from '@troc/shared'

/**
 * La question posée au comptoir quand une pièce a droit à une baisse.
 *
 * La boutique vend au prix de l'étiquette : les étiquettes ne sont pas
 * refaites, et une caisse qui démarquerait toute seule annoncerait un prix que
 * le client ne lit nulle part. Mais le contrat promet au déposant une baisse
 * mensuelle, et le vendeur doit pouvoir l'accorder sans la calculer de tête.
 *
 * D'où cette fenêtre, qui s'ouvre à l'encodage d'un article démarqué : elle
 * annonce le prix de l'étiquette, celui que le contrat prévoit, et laisse
 * choisir entre les deux. Un geste pour accepter, un geste pour refuser, et de
 * quoi se placer où l'on veut si le client négocie entre les deux.
 */

export interface ArticleADemarquer {
  ref: string
  titre: string
  /** Ancienneté en mois révolus, telle qu'affichée ailleurs. */
  mois: number
  /** Décote prévue au contrat, en fraction (0.2 = −20 %). */
  decoteConseillee: number
  /** Le prix écrit sur l'étiquette. */
  prixEtiquetteCentimes: number
  /** Ce que le contrat prévoit aujourd'hui. */
  prixDemarqueCentimes: number
}

/**
 * Les prix ronds à proposer entre les deux extrêmes.
 *
 * Ni un curseur ni une liste de pourcentages : au comptoir on négocie en
 * euros. On en propose au plus quatre — au-delà, l'œil ne choisit plus, il
 * cherche.
 */
function pallierIntermediaires(minCentimes: number, maxCentimes: number): number[] {
  const ecart = maxCentimes - minCentimes
  if (ecart < 200) return []
  const nombre = Math.min(4, Math.floor(ecart / 100) - 1)
  if (nombre < 1) return []
  const pas = ecart / (nombre + 1)
  const valeurs = new Set<number>()
  for (let i = 1; i <= nombre; i += 1) {
    valeurs.add(Math.round((minCentimes + pas * i) / 100) * 100)
  }
  return [...valeurs].filter((v) => v > minCentimes && v < maxCentimes).sort((a, b) => a - b)
}

export function DemandeDemarque({
  article,
  onChoisir,
  onAnnuler,
  libelleAnnuler = 'Retirer du ticket',
}: {
  article: ArticleADemarquer
  /** Le prix retenu pour cette vente. L'article en base n'est pas modifié. */
  onChoisir: (prixCentimes: number) => void
  onAnnuler: () => void
  /**
   * Ce que « ne rien changer » veut dire ici. À l'encodage, la pièce sortait du
   * ticket ; ouverte depuis la liste des baisses dues, elle y reste au prix de
   * l'étiquette.
   */
  libelleAnnuler?: string
}) {
  const [autre, setAutre] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const accepter = useRef<HTMLButtonElement>(null)

  const bas = article.prixDemarqueCentimes
  const haut = article.prixEtiquetteCentimes
  const intermediaires = useMemo(() => pallierIntermediaires(bas, haut), [bas, haut])

  // Le bouton d'acceptation prend le clavier : au comptoir, la réponse la plus
  // fréquente doit se donner sans quitter la douchette des yeux.
  useEffect(() => {
    accepter.current?.focus()
  }, [])

  useEffect(() => {
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') {
        evenement.preventDefault()
        onAnnuler()
      }
    }
    window.addEventListener('keydown', auClavier)
    return () => window.removeEventListener('keydown', auClavier)
  }, [onAnnuler])

  const validerAutre = () => {
    const nettoye = autre.replace(/[€\s ]/g, '').replace(',', '.')
    const valeur = Number.parseFloat(nettoye)
    if (!Number.isFinite(valeur)) {
      setErreur('Indiquez un prix.')
      return
    }
    const centimes = Math.round(valeur * 100)
    if (centimes < bas || centimes > haut) {
      setErreur(`Entre ${eur(bas)} et ${eur(haut)}.`)
      return
    }
    onChoisir(centimes)
  }

  return (
    <div className="voile voile-centre" onMouseDown={onAnnuler}>
      <div
        className="fenetre-demarque"
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-demarque"
        onMouseDown={(evenement) => evenement.stopPropagation()}
      >
        <div className="fenetre-demarque-entete">
          <h3 id="titre-demarque">Cette pièce a baissé</h3>
          <p className="sourdine">
            {article.ref} · {article.titre}
          </p>
        </div>

        <div className="fenetre-demarque-prix">
          <span className="prix-barre">{eur(haut)}</span>
          <span className="prix-du-jour chiffre">{eur(bas)}</span>
          <span className="sourdine">
            en rayon depuis {article.mois} mois · le contrat prévoit −
            {Math.round(article.decoteConseillee * 100)} %
          </span>
        </div>

        <button
          ref={accepter}
          type="button"
          className="btn btn-primaire btn-demarque-accepter"
          onClick={() => onChoisir(bas)}
        >
          Accepter la baisse · {eur(bas)}
        </button>

        {intermediaires.length > 0 && (
          <div className="fenetre-demarque-entre">
            <div className="etiquette">Ou entre les deux</div>
            <div className="fenetre-demarque-crans">
              {intermediaires.map((centimes) => (
                <button
                  key={centimes}
                  type="button"
                  className="btn"
                  onClick={() => onChoisir(centimes)}
                >
                  {eur(centimes)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="fenetre-demarque-libre">
          <label htmlFor="autre-prix" className="etiquette">
            Autre prix
          </label>
          <input
            id="autre-prix"
            className="saisie"
            inputMode="decimal"
            placeholder={`${eur(bas)} à ${eur(haut)}`}
            value={autre}
            onChange={(evenement) => {
              setAutre(evenement.target.value)
              setErreur(null)
            }}
            onKeyDown={(evenement) => {
              if (evenement.key === 'Enter') {
                evenement.preventDefault()
                validerAutre()
              }
            }}
          />
          <button type="button" className="btn" onClick={validerAutre}>
            Appliquer
          </button>
        </div>
        {erreur && <p className="sourdine erreur-demarque">{erreur}</p>}

        <div className="fenetre-demarque-pied">
          <button type="button" className="btn" onClick={() => onChoisir(haut)}>
            Garder le prix de l’étiquette · {eur(haut)}
          </button>
          <button type="button" className="btn btn-fantome" onClick={onAnnuler}>
            {libelleAnnuler}
          </button>
        </div>
      </div>
    </div>
  )
}
